"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTransaction = void 0;
const useAuth_1 = require("../useAuth");
const out_1 = require("@multiversx/sdk-core/out");
const primitives_1 = require("@multiversx/sdk-network-providers/out/primitives");
const useNetworkProvider_1 = require("../useNetworkProvider");
const types_1 = require("@elrond-giants/erdjs-auth/dist/types");
const network_1 = require("@elrond-giants/erdjs-auth/dist/network");
const utils_1 = require("../utils");
const react_1 = require("react");
const useTransaction = () => {
    const { address, nonce, provider, env, guardian, use2FABrowserInput, increaseNonce } = (0, useAuth_1.useAuth)();
    const networkProvider = (0, useNetworkProvider_1.useNetworkProvider)();
    const requires2FACode = (0, react_1.useMemo)(() => {
        if (!provider) {
            return false;
        }
        return (0, utils_1.shouldApplyGuardianSignature)(provider.getType()) && guardian.guarded;
    }, [provider, guardian]);
    const makeTransaction = (txData) => __awaiter(void 0, void 0, void 0, function* () {
        if (!provider) {
            throw new Error("No auth provider! Make sure the account is authenticated.");
        }
        const { onBeforeSign, onSigned, webReturnUrl, transaction, guard2FACode } = txData;
        let tx = yield prepareTransaction(transaction);
        if (provider.getType() === types_1.AuthProviderType.WEBWALLET) {
            yield sendWebTransaction(tx, webReturnUrl !== null && webReturnUrl !== void 0 ? webReturnUrl : window.location.href);
            return "";
        }
        if ((0, utils_1.requiresGuardianSignature)(tx)) {
            const guardedTx = yield guardTransactions([tx], guard2FACode);
            tx = guardedTx[0];
        }
        let signedTx = tx;
        if (needsSigning(provider.getType())) {
            if (typeof onBeforeSign === "function") {
                onBeforeSign();
            }
            signedTx = yield provider.signTransaction(tx);
            if (typeof onSigned === "function") {
                onSigned();
            }
        }
        try {
            // @ts-ignore
            const result = yield networkProvider.sendTransaction(signedTx);
            increaseNonce();
            return result;
        }
        catch (e) {
            throw e;
        }
    });
    const makeTransactions = (txData) => __awaiter(void 0, void 0, void 0, function* () {
        if (!provider) {
            throw new Error("No auth provider! Make sure the account is authenticated.");
        }
        if (provider.getType() === types_1.AuthProviderType.WEBWALLET) {
            throw new Error("Web wallet does not support multiple transactions yet.");
        }
        const { onBeforeSign, onSigned, webReturnUrl, transactions, guard2FACode } = txData;
        let txs = yield Promise.all(transactions.map((tx) => __awaiter(void 0, void 0, void 0, function* () {
            return yield prepareTransaction(tx);
        })));
        if (guardian.guarded) {
            txs = yield guardTransactions(txs, guard2FACode);
        }
        let signedTxs = txs;
        if (needsSigning(provider.getType())) {
            if (typeof onBeforeSign === "function") {
                onBeforeSign();
            }
            signedTxs = yield provider.signTransactions(txs);
            if (typeof onSigned === "function") {
                onSigned();
            }
        }
        return yield networkProvider.sendTransactions(signedTxs);
    });
    const prepareTransaction = (transaction) => __awaiter(void 0, void 0, void 0, function* () {
        let tx;
        if (Object.getPrototypeOf(transaction).constructor.name === out_1.Transaction.name) {
            tx = transaction;
        }
        else {
            // @ts-ignore
            tx = yield buildTransaction(transaction);
        }
        if (guardian.guarded && tx.getGuardian().bech32() === "") {
            tx.setGuardian(guardian.activeGuardian.address);
            tx.setVersion(out_1.TransactionVersion.withTxOptions());
            const options = tx.getOptions();
            if (!options.isWithGuardian()) {
                options.setWithGuardian();
                tx.setOptions(options);
            }
        }
        return tx;
    });
    const whenCompleted = (txHash, options = {}) => {
        const { interval, timeout, patience } = options;
        const watcher = new out_1.TransactionWatcher(networkProvider, {
            pollingIntervalMilliseconds: interval,
            timeoutMilliseconds: timeout,
            patienceMilliseconds: patience,
        });
        return watcher.awaitCompleted({
            getHash: () => ({ hex: () => txHash }),
        });
    };
    const sendWebTransaction = (tx, returnUrl) => __awaiter(void 0, void 0, void 0, function* () {
        let plainTx = tx.toPlainObject();
        if (plainTx.data) {
            plainTx.data = Buffer.from(plainTx.data, "base64").toString();
        }
        let urlString = `receiver=${plainTx.receiver}&value=${plainTx.value}`
            + `&gasLimit=${plainTx.gasLimit}&nonce=${plainTx.nonce}`
            + `&data=${plainTx.data}`
            + `&callbackUrl=${returnUrl}`;
        const networkUrl = new URL(network_1.network[env].walletAddress);
        window.location.href = `${networkUrl.origin}/hook/transaction/?${urlString}`;
        return tx;
    });
    const signWebTransactions = (txs, returnUrl) => __awaiter(void 0, void 0, void 0, function* () {
        // If no return url is provided, we will use the default one set on provider.
        // We'll make the process cleaner in a future release.
        if (!returnUrl) {
            return provider.signTransactions(txs);
        }
        return provider.getBaseProvider().signTransactions(txs, {
            callbackUrl: returnUrl
        });
    });
    const buildTransaction = (txData) => __awaiter(void 0, void 0, void 0, function* () {
        let { data, value, receiver, gasLimit, chainId, options, guardian: _guardian, version, nonce: _nonce } = txData;
        const payload = data instanceof out_1.TransactionPayload ? data : new out_1.TransactionPayload(data);
        if (value) {
            value = value instanceof out_1.TokenTransfer ? value : out_1.TokenTransfer.egldFromAmount(value);
        }
        if (!gasLimit) {
            let gas = new out_1.GasEstimator().forEGLDTransfer(payload.length()).valueOf();
            if (guardian.guarded) {
                gas += 50000;
            }
            gasLimit = gas;
        }
        if (!chainId) {
            chainId = yield getChainId();
        }
        return new out_1.Transaction({
            chainID: chainId,
            data: payload,
            gasLimit,
            guardian: _guardian ? new out_1.Address(_guardian) : undefined,
            nonce: new primitives_1.Nonce(_nonce !== null && _nonce !== void 0 ? _nonce : nonce),
            options,
            receiver: new out_1.Address(receiver),
            sender: out_1.Address.fromBech32(address !== null && address !== void 0 ? address : ""),
            value,
            version,
        });
    });
    const getChainId = () => __awaiter(void 0, void 0, void 0, function* () {
        const { ChainID } = yield networkProvider.getNetworkConfig();
        return ChainID;
    });
    const needsSigning = (providerType) => {
        return [
            types_1.AuthProviderType.PEM,
            types_1.AuthProviderType.LEDGER,
            types_1.AuthProviderType.EXTENSION,
            types_1.AuthProviderType.WALLET_CONNECT,
            types_1.AuthProviderType.WEBWALLET,
            types_1.AuthProviderType.WEBVIEW
        ].includes(providerType);
    };
    const guardTransactions = (transactions, code) => __awaiter(void 0, void 0, void 0, function* () {
        if (code) {
            return (0, utils_1.guardTransactions)(transactions, code, env);
        }
        // If the code was not provided and the provider can handle
        // the guardian signature, we'll let the provider handle it.
        if (!(0, utils_1.shouldApplyGuardianSignature)(provider.getType())) {
            return transactions;
        }
        if (!use2FABrowserInput) {
            // We won't guard transactions if there is no code provided and the
            // 2FA browser input is disabled.
            return transactions;
        }
        const inputCode = prompt("Please enter your 2FA code");
        if (inputCode) {
            return (0, utils_1.guardTransactions)(transactions, inputCode, env);
        }
        return transactions;
    });
    return { requires2FACode, makeTransaction, makeTransactions, whenCompleted };
};
exports.useTransaction = useTransaction;
